import moment from "moment-timezone";
import React from "react";
import styled from "styled-components";

import { HackerJudgingStatus } from "src/api/types.generated";
import { JudgingFloorPlanFloor2, JudgingFloorPlanFloor3 } from "src/static/img";

const formatJudgingTime = (isoTime?: string | null): string => {
  if (!isoTime) return "—";
  const t = moment.tz(isoTime, "America/Toronto");
  return t.isValid() ? t.format("h:mm A") : "—";
};

const R1_STATUSES = new Set([
  HackerJudgingStatus.Round_1Scheduled,
  HackerJudgingStatus.Round_1WaitingRoom,
  HackerJudgingStatus.Round_1InJudging,
]);

const R2_STATUSES = new Set([
  HackerJudgingStatus.Round_2Scheduled,
  HackerJudgingStatus.Round_2CheckedIn,
  HackerJudgingStatus.Round_2InJudging,
]);

type Props = {
  status?: HackerJudgingStatus;
  judgingLocation?: string | null;
  judgingTime?: string | null;
};

export const JudgingDetailsCard: React.FC<Props> = ({
  status,
  judgingLocation,
  judgingTime,
}) => {
  const isFinalist = status === HackerJudgingStatus.FinalistNotified;
  const isR1 = status !== undefined && R1_STATUSES.has(status);
  const isR2 = status !== undefined && R2_STATUSES.has(status);

  if (!isFinalist && !isR1 && !isR2) return null;

  const formattedTime = formatJudgingTime(judgingTime);

  return (
    <>
      <Card>
        <CardTitle>Judging Details</CardTitle>
        {isFinalist ? (
          <>
            <DetailRow>
              <DetailLabel>Location</DetailLabel>
              <DetailValue>Lazaridis Hall</DetailValue>
            </DetailRow>
            <Divider />
            <DetailRow>
              <DetailLabel>Time</DetailLabel>
              <DetailValue>ASAP!</DetailValue>
            </DetailRow>
          </>
        ) : isR1 ? (
          <>
            <DetailRow>
              <DetailLabel>Waiting Room</DetailLabel>
              <DetailValue>{judgingLocation ?? "—"}</DetailValue>
            </DetailRow>
            <Divider />
            <DetailRow>
              <DetailLabel>Judging Time</DetailLabel>
              <DetailValue>{formattedTime}</DetailValue>
            </DetailRow>
          </>
        ) : (
          <>
            <DetailRow>
              <DetailLabel>Location</DetailLabel>
              <DetailValue>
                PSE Second Floor Events Space (Sponsor Bay)
              </DetailValue>
            </DetailRow>
            <Divider />
            <DetailRow>
              <DetailLabel>Table Number</DetailLabel>
              <DetailValue>{judgingLocation ?? "—"}</DetailValue>
            </DetailRow>
            <Divider />
            <DetailRow>
              <DetailLabel>Check-in Time</DetailLabel>
              <DetailValue>{formattedTime}</DetailValue>
            </DetailRow>
          </>
        )}
      </Card>

      <Card>
        <CardTitle>Floor Plan</CardTitle>
        <FloorPlanHint>
          Swipe to pan a map, or tap it to open the full-size view.
        </FloorPlanHint>
        <FloorPlanList>
          <FloorPlanLink
            href={JudgingFloorPlanFloor2}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FloorPlanImg
              src={JudgingFloorPlanFloor2}
              alt="E5 and PSE (E7) Floor 2 judging floor plan"
            />
          </FloorPlanLink>
          <FloorPlanLink
            href={JudgingFloorPlanFloor3}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FloorPlanImg
              src={JudgingFloorPlanFloor3}
              alt="E5 and PSE (E7) Floor 3 judging floor plan"
            />
          </FloorPlanLink>
        </FloorPlanList>
      </Card>
    </>
  );
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 16px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border-radius: 8px;
`;

const CardTitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
`;

const DetailLabel = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-align: right;
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  width: 100%;
`;

const FloorPlanHint = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const FloorPlanList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const FloorPlanLink = styled.a`
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  border-radius: 4px;
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => theme.globalConstants.color.brandPrimary};
    outline-offset: 2px;
  }
`;

const FloorPlanImg = styled.img`
  display: block;
  width: max(100%, 680px);
  height: auto;
`;
